import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const MENU_MIN_WIDTH = 320;
const VIEWPORT_MARGIN = 12;

function SaveIcon() {
  return (
    <svg
      className="approve-save-menu-icon"
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
      <polyline points="17 21 17 13 7 13 7 21" />
      <polyline points="7 3 7 8 15 8" />
    </svg>
  );
}

function LockIcon() {
  return (
    <svg
      className="approve-save-menu-icon"
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );
}

function computeMenuPosition(anchorEl, menuEl) {
  if (!anchorEl) {
    return { top: VIEWPORT_MARGIN, left: VIEWPORT_MARGIN };
  }

  const anchorRect = anchorEl.getBoundingClientRect();
  const menuHeight = menuEl?.offsetHeight || 180;
  const menuWidth = Math.max(
    anchorRect.width,
    menuEl?.offsetWidth || MENU_MIN_WIDTH,
    MENU_MIN_WIDTH,
  );

  let left = anchorRect.right - menuWidth;
  left = Math.max(
    VIEWPORT_MARGIN,
    Math.min(left, window.innerWidth - menuWidth - VIEWPORT_MARGIN),
  );

  let top = anchorRect.top - menuHeight - 8;
  if (top < VIEWPORT_MARGIN) {
    top = anchorRect.bottom + 8;
  }

  return { top, left, width: menuWidth };
}

function ApproveSaveDropdown({
  open,
  anchorRef,
  onClose,
  onApprove,
  onSaveAndLock,
  saving = false,
  disabled = false,
  approveLabel = 'Approve & Save',
  showSaveAndLock = true,
}) {
  const menuRef = useRef(null);
  const [position, setPosition] = useState({ top: 0, left: 0, width: MENU_MIN_WIDTH });

  const updatePosition = useCallback(() => {
    const anchorEl = anchorRef?.current;
    const menuEl = menuRef.current;
    setPosition(computeMenuPosition(anchorEl, menuEl));
  }, [anchorRef]);

  useEffect(() => {
    if (!open) return undefined;

    updatePosition();
    const onResize = () => updatePosition();
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onResize, true);

    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onResize, true);
    };
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open) return undefined;

    requestAnimationFrame(() => updatePosition());

    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };

    const onPointerDown = (event) => {
      const menuEl = menuRef.current;
      const anchorEl = anchorRef?.current;
      if (menuEl?.contains(event.target) || anchorEl?.contains(event.target)) {
        return;
      }
      onClose();
    };

    window.addEventListener('keydown', onKeyDown);
    document.addEventListener('mousedown', onPointerDown);

    return () => {
      window.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('mousedown', onPointerDown);
    };
  }, [open, onClose, anchorRef, updatePosition]);

  const handleApprove = async () => {
    onClose();
    await onApprove();
  };

  const handleSaveAndLock = async () => {
    onClose();
    await onSaveAndLock();
  };

  if (!open) return null;

  return createPortal(
    <>
      <div className="approve-save-overlay" aria-hidden="true" />
      <div
        ref={menuRef}
        className="approve-save-menu"
        role="menu"
        aria-label="Save options"
        style={{
          top: position.top,
          left: position.left,
          width: position.width,
        }}
      >
        <button
          type="button"
          className="approve-save-menu-item"
          role="menuitem"
          onClick={handleApprove}
          disabled={saving || disabled}
        >
          <SaveIcon />
          <span className="approve-save-menu-text">
            <span className="approve-save-menu-title">{approveLabel}</span>
            <span className="approve-save-menu-description">
              Saves this translation to Translation Memory
            </span>
          </span>
        </button>
        {showSaveAndLock && (
          <>
            <div className="approve-save-menu-divider" role="separator" />
            <button
              type="button"
              className="approve-save-menu-item"
              role="menuitem"
              onClick={handleSaveAndLock}
              disabled={saving || disabled}
            >
              <LockIcon />
              <span className="approve-save-menu-text">
                <span className="approve-save-menu-title">Save &amp; Lock</span>
                <span className="approve-save-menu-description">
                  Saves this translation to Translation Memory and locks the translation
                </span>
              </span>
            </button>
          </>
        )}
      </div>
    </>,
    document.body,
  );
}

export default ApproveSaveDropdown;
